import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { BrandLogo } from '@/components/brand/BrandLogo';
import { Button } from '@/components/ui/button';
import { useAuth } from '@/contexts/AuthContext';
import { usePageTitle } from '@/hooks/usePageTitle';

/**
 * Página 404 do site público. Import estático em App.tsx (mesma exceção de
 * LoginPage): não há shell com <Suspense> nesta rota, e o catch-all não pode
 * pintar a Landing — senão URL inexistente vira conteúdo duplicado.
 */
export function NotFoundPage() {
  usePageTitle('Página não encontrada');
  const { isAuthenticated, isSuperAdmin } = useAuth();
  const destino = !isAuthenticated ? '/' : isSuperAdmin ? '/superadmin' : '/painel';

  useEffect(() => {
    const meta = document.createElement('meta');
    meta.setAttribute('name', 'robots');
    meta.setAttribute('content', 'noindex, nofollow');
    document.head.appendChild(meta);
    return () => {
      meta.remove();
    };
  }, []);

  return (
    <div className="site-public flex min-h-screen flex-col">
      <header className="border-b border-[#d9d4cc]">
        <div className="mx-auto flex h-[4.25rem] w-full max-w-6xl items-center px-4">
          <Link
            to={destino}
            className="flex items-center focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#2b9ce1]"
          >
            <BrandLogo variant="compact" className="h-8" />
          </Link>
        </div>
      </header>

      <main className="flex flex-1 items-center px-4 py-12 sm:px-10">
        <div
          className="mx-auto w-full max-w-[28rem]"
          style={{ animation: 'fade-in-up 220ms ease-out both' }}
        >
          <p className="text-sm text-[#5c5852]">Página não encontrada</p>
          <h1 className="mt-2 font-display text-[clamp(2rem,4vw,3.15rem)] font-semibold leading-[1.08] tracking-tight text-[#16181d]">
            Parece que esta página não existe.
          </h1>
          <div className="mt-8">
            <Button asChild variant="default">
              <Link to={destino}>Ir à página principal</Link>
            </Button>
          </div>
        </div>
      </main>
    </div>
  );
}
